/**
 * @file useBuilderPages.ts
 * @description Страницы сайта в конструкторе (phase19.16 Р1-6а; логика
 * бывших `SitePagesListPage` и `SitePageBuilderPage`): список страниц,
 * открытая страница с блоками, её настройки, скрытие, создание и
 * удаление.
 *
 * Открытая страница — по выбору в адресе; без выбора — главная. Блоки
 * правит `useBlockActions` (поверх `setPage`). После правки, которую не
 * показывает сама канва, растёт `canvasVersion` — канва перерисовывается.
 * Тексты ошибок — словами владельца; техническое сообщение сервера не
 * показывается, если для кода есть своя фраза.
 *
 * phase19.16 Р2а: на экране «Страница товара» открыта страница-шаблон
 * (`GET /admin/site/templates/product`): её блоки правятся как обычные, а
 * рядом — сколько товаров получат шаблон и товар для примера (`template`).
 *
 * phase19.16 Р3а: правки страницы — в черновик; публикация, «Отменить
 * правки» и версии — `usePagePublishing` (с отпечатком черновика). Здесь
 * остались скрыть и удалить (мягко, SB128) — без отпечатка.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../../lib/api-client";
import { sitePagePublish } from "../../lib/api-site-page-publish";
import type { SiteRedirectTargetInput } from "../../lib/api-site-redirects";
import { siteTemplates, type ProductTemplateResponse } from "../../lib/api-site-templates";
import type { BuilderScreenKey } from "../../lib/site-builder/selection";
import {
  sitePages,
  type CreateSitePageInput,
  type SitePageDetail,
  type SitePageListItem,
  type UpdateSitePageInput,
} from "../../lib/api-site-pages";
import { invalidatePickList } from "./usePickList";

const LOAD_FAILED = "Не удалось загрузить страницы. Сайт работает как прежде — ничего не потеряно.";
const TEMPLATE_FAILED = "Шаблон страницы товара сейчас не загружается — проверьте позже. Сайт работает как прежде.";

/** Шаблон страницы товара рядом со страницей-шаблоном: охват и товар для примера. */
export type TemplateMeta = Omit<ProductTemplateResponse, "page">;

/** Ключ открытой страницы: id страницы или шаблон экрана «Страница товара». */
const TEMPLATE_KEY = "template:PRODUCT";

export interface BuilderPagesApi {
  list: SitePageListItem[] | null;
  listError: string | null;
  page: SitePageDetail | null;
  pageError: string | null;
  setPage: (page: SitePageDetail) => void;
  /** Р2а: открыт шаблон страницы товара — его охват и товар для примера; иначе `null`. */
  template: TemplateMeta | null;
  /** Ответ сервера с шаблоном (сброс, раскладка) — в состояние без перечитывания. */
  applyTemplate: (res: ProductTemplateResponse) => void;
  reloadList: () => Promise<void>;
  reloadPage: () => Promise<void>;
  canvasVersion: number;
  bumpCanvas: () => void;
  update: (input: UpdateSitePageInput) => Promise<SitePageDetail>;
  /** Скрыть страницу с сайта (право публикации, SB128); версии сохраняются. */
  unpublish: () => Promise<SitePageDetail>;
  create: (input: CreateSitePageInput) => Promise<SitePageDetail>;
  /** Р3в (SB128): `redirect` — переадресация со старого адреса на выбранную цель. */
  remove: (redirect?: SiteRedirectTargetInput | null) => Promise<void>;
}

/** Текст ошибки сервера или запасной. */
export function pageErrorText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

/** Страницы сайта меняет сотрудник с правом на контент (без него сервер ответит 403). */
export const PAGES_FORBIDDEN_TEXT = "Страницы сайта меняет сотрудник с правом на контент сайта. Оформление, меню и настройки — в разделах «Весь сайт».";

export function usePageList(enabled = true) {
  const [list, setList] = useState<SitePageListItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const reloadList = useCallback(async () => {
    try {
      setList(await sitePages.list());
      setListError(null);
    } catch (err) {
      setListError(pageErrorText(err, LOAD_FAILED));
    }
  }, []);
  useEffect(() => {
    if (enabled) void reloadList();
  }, [enabled, reloadList]);
  return { list, listError, reloadList };
}

export function useBuilderPages(pageId: string | null, screen: BuilderScreenKey | null, enabled = true): BuilderPagesApi {
  const { list, listError, reloadList } = usePageList(enabled);
  const [page, setPage] = useState<SitePageDetail | null>(null);
  const [template, setTemplate] = useState<TemplateMeta | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [canvasVersion, setCanvasVersion] = useState(1);

  // Без выбора — главная страница магазина; экран «Страница товара» — шаблон.
  const targetId = useMemo(() => {
    if (!enabled) return null;
    if (screen === "product") return TEMPLATE_KEY;
    if (screen) return null;
    return pageId ?? list?.find((p) => p.kind === "SYSTEM_HOME")?.id ?? null;
  }, [pageId, screen, enabled, list]);
  // Ключ, по которому загружена текущая `page` (у шаблона id страницы ≠ ключ).
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const loadedKeyRef = useRef(loadedKey);
  loadedKeyRef.current = loadedKey;

  const applyTemplate = useCallback((res: ProductTemplateResponse) => {
    const { page: templatePage, ...meta } = res;
    setPage(templatePage);
    setTemplate(meta);
    setLoadedKey(TEMPLATE_KEY);
    setPageError(null);
  }, []);

  // Быстрая смена страниц: ответ по прежней странице не должен лечь на новую.
  const currentTarget = useRef(targetId);
  currentTarget.current = targetId;
  const reloadPage = useCallback(async () => {
    if (!targetId) return;
    try {
      if (targetId === TEMPLATE_KEY) {
        const res = await siteTemplates.product();
        if (currentTarget.current === targetId) applyTemplate(res);
        return;
      }
      const loaded = await sitePages.findOne(targetId);
      if (currentTarget.current !== targetId) return;
      setPage(loaded);
      setLoadedKey(targetId);
      setPageError(null);
    } catch (err) {
      if (currentTarget.current !== targetId) return;
      if (targetId === TEMPLATE_KEY) setPageError(TEMPLATE_FAILED);
      else
        setPageError(
          err instanceof ApiError && err.code === "site_page_not_found"
            ? "Страницу удалили — выберите другую в списке страниц."
            : pageErrorText(err, LOAD_FAILED),
        );
    }
  }, [applyTemplate, targetId]);

  useEffect(() => {
    if (targetId !== TEMPLATE_KEY) setTemplate(null);
    if (!targetId) {
      setPage(null);
      setLoadedKey(null);
      return;
    }
    if (loadedKeyRef.current !== targetId) {
      setPage(null);
      setLoadedKey(null);
    }
    setPageError(null);
    void reloadPage();
  }, [targetId, reloadPage]);

  const bumpCanvas = useCallback(() => setCanvasVersion((v) => v + 1), []);

  /** Правка, которая меняет и список страниц (название, адрес, видимость). */
  const commit = useCallback(
    async (run: () => Promise<SitePageDetail>) => {
      const next = await run();
      setPage(next);
      void reloadList();
      setCanvasVersion((v) => v + 1);
      return next;
    },
    [reloadList],
  );

  // Правки — по id загруженной страницы (у шаблона ключ адреса не id).
  const openId = loadedKey === targetId ? (page?.id ?? "") : "";
  const update = useCallback((input: UpdateSitePageInput) => commit(() => sitePages.update(openId, input)), [commit, openId]);
  const unpublish = useCallback(() => commit(() => sitePagePublish.unpublish(openId)), [commit, openId]);
  const create = useCallback(
    async (input: CreateSitePageInput) => {
      const created = await sitePages.create(input);
      invalidatePickList("sitePages");
      await reloadList();
      return created;
    },
    [reloadList],
  );
  const remove = useCallback(async (redirect: SiteRedirectTargetInput | null = null) => {
    if (!openId) return;
    await sitePagePublish.remove(openId, redirect);
    invalidatePickList("sitePages");
    setPage(null);
    await reloadList();
  }, [reloadList, openId]);

  return {
    list,
    listError,
    page: targetId !== null && loadedKey === targetId ? page : null,
    pageError,
    setPage,
    template: targetId === TEMPLATE_KEY && loadedKey === TEMPLATE_KEY ? template : null,
    applyTemplate,
    reloadList,
    reloadPage,
    canvasVersion,
    bumpCanvas,
    update,
    unpublish,
    create,
    remove,
  };
}
